// File: /var/www/app2/frontend/src/components/Help.tsx

import React from "react";
import { User } from "../App";
import "../styles/Help.css";

interface HelpProps {
  user: User;
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ title, children, defaultOpen }: SectionProps): React.ReactElement {
  return (
    <details className="help-section" open={defaultOpen}>
      <summary>{title}</summary>
      <div className="help-section-body">{children}</div>
    </details>
  );
}

function Help({ user }: HelpProps): React.ReactElement {
  const isAdmin =
    user.role === "admin" || user.role === "superadmin" || user.role === "system_admin";
  const isSystemAdmin = user.role === "system_admin";
  const isSuperAdmin = user.role === "superadmin";

  return (
    <div className="help-page">
      <h2>Office App — Help &amp; Training</h2>
      <p className="help-intro">
        A quick reference for the Horn of Africa <strong>office</strong> app
        (this site — <code>hoa-office.adams-ai.com</code>). Field missionaries
        submit their reports from the separate <strong>missions app</strong>{" "}
        at <code>hoa-missions.adams-ai.com</code>, which has its own Help
        page.
      </p>

      <Section title="Logging in" defaultOpen>
        <ul>
          <li>
            Go to <code>hoa-office.adams-ai.com</code> and sign in with your
            username or email and password.
          </li>
          <li>
            Forgot your password? Click <strong>Forgot password?</strong> on
            the login screen and enter your username or email exactly as it
            was set up for you (case doesn't matter, but it must match — if
            nothing arrives after a minute or two, double-check for typos
            before trying again).
          </li>
          <li>
            You can change your password any time from{" "}
            <strong>Change Password</strong> in the top-right menu.
          </li>
          <li>
            Note: your office-app password is separate from your
            missions-app password — changing one does not change the other.
          </li>
        </ul>
      </Section>

      <Section title="Dashboard">
        <ul>
          <li>
            Shows totals and trends for the currently active country (see{" "}
            <strong>Countries &amp; switching</strong> below).
          </li>
        </ul>
      </Section>

      <Section title="Reviewing and editing reports">
        <ul>
          <li>
            <strong>Reports</strong> lists everything submitted from the
            missions app. Click a report to open it.
          </li>
          <li>
            From a report in <strong>Review</strong> or{" "}
            <strong>Rejected</strong> status, admins can click{" "}
            <strong>Edit Report</strong> to correct its contents directly,
            or use <strong>Approve</strong> / <strong>Action Required</strong>{" "}
            to move it along the workflow. There's no separate "reject"
            button — <strong>Action Required</strong> is the equivalent, and
            sends it back to the submitter.
          </li>
          <li>
            <strong>Revert to Review</strong> is available on
            approved/rejected/action-required reports if you need to undo a
            status change.
          </li>
          <li>
            Going back from a report to the list now returns you to
            approximately where you were scrolled, instead of jumping to the
            top.
          </li>
        </ul>
      </Section>

      <Section title="Missionaries">
        <ul>
          <li>
            Manage the roster of field missionaries reports are attributed
            to.
          </li>
        </ul>
      </Section>

      {isAdmin && (
        <Section title="Users">
          <ul>
            <li>
              Create accounts, edit roles, deactivate accounts, and assign a
              home country to <strong>Supporter</strong> and{" "}
              <strong>Field User</strong> roles (a single country each).
            </li>
            <li>
              <strong>System Admin</strong> and <strong>Country Manager</strong>{" "}
              roles can be assigned to more than one country — that's done
              from the <strong>Countries</strong> page, not on the user's edit
              form, since a single dropdown can't represent multiple
              countries. Save the user first, then assign countries there.
            </li>
            <li>
              Changing a user's <strong>role</strong> or{" "}
              <strong>country</strong> now asks you to confirm before saving
              — this also guards against an accidental mouse-wheel scroll
              over the dropdown silently changing the selected value while
              you're scrolling the page.
            </li>
          </ul>
        </Section>
      )}

      {isAdmin && (
        <Section title="Supporters">
          <ul>
            <li>Manage supporter contacts and their assignments.</li>
          </ul>
        </Section>
      )}

      {isAdmin && (
        <Section title="Support requests">
          <ul>
            <li>
              Submit a bug report, feature request, or change request from{" "}
              <strong>Support Requests</strong> — you'll get a confirmation
              email, sent from <code>noreply@hoa.adams-ai.com</code>.
            </li>
            <li>
              Admins can also view and respond to requests submitted by
              others from this same page.
            </li>
          </ul>
        </Section>
      )}

      {isAdmin && (
        <Section title="Media library">
          <ul>
            <li>
              Browse and manage photos synced in from reports and village
              verification.
            </li>
          </ul>
        </Section>
      )}

      {isSystemAdmin && (
        <Section title="Countries & switching">
          <ul>
            <li>
              <strong>Countries</strong> (System Admin only) is where you add
              countries and assign Country Managers to them.
            </li>
            <li>
              The country switcher in the top nav changes which country's
              data you're viewing. If you're only assigned to one country, it
              won't show a choice.
            </li>
            <li>
              <strong>Important:</strong> the active country is shared per
              browser, not per tab. If you have this app open in two tabs of
              the same browser and switch countries in one, the other tab
              can pick up that same change on its next action. If you need
              to work in two countries side by side, use two different
              browsers (or one normal + one private/incognito window) rather
              than two tabs of the same one.
            </li>
          </ul>
        </Section>
      )}

      {isSuperAdmin && (
        <Section title="Security dashboard">
          <ul>
            <li>
              Superadmin-only view of login activity and account security
              events.
            </li>
          </ul>
        </Section>
      )}

      <Section title="Tips & known limitations">
        <ul>
          <li>
            Native browser dropdown menus (Role, Country, etc.) can
            occasionally change their selected value if you scroll your
            mouse wheel while hovering over one without clicking elsewhere
            first — this is a browser quirk, not an app bug. Role and
            Country changes now always ask you to confirm before saving, so
            an accidental scroll won't silently take effect.
          </li>
          <li>
            See the note above under <strong>Countries &amp; switching</strong>{" "}
            about multiple browser tabs sharing one active country.
          </li>
        </ul>
      </Section>
    </div>
  );
}

export default Help;
